import React, { useState, memo, useCallback } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './JobStatusList.module.scss';
import classNames from 'classnames/bind';
import { BaseSort } from '~/components/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import MenuOption from '~/components/equipments/menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import usePermission from '~/hooks/usePermission';

import { PERMISSIONS } from '~/utils/constants/permission';
import { IJobStatus } from '~/utils/interface/job-status';
import { jobStatusActions } from '~/thunks/job-status.ts/jobStatusSlice';
import { Checkbox } from '@mui/material';
import dayjs from 'dayjs';
import { handleUpdateposition } from '~/thunks/job-status.ts/jobStatusThunk';

const cx = classNames.bind(styles);

type JobStatusListProps = {
  data?: IJobStatus;
  onOpenEdit: (id: string) => void;
};

function JobStatusList(props: JobStatusListProps) {
  const permissions = usePermission();
  const { t } = useTranslation(['admin/job-status']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [selected, setSelected] = useState<string[]>([]);
  const isSelected = (id: string) => selected.indexOf(id) !== -1;
  const [selectAll, setSelectAll] = useState(false);
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const { jobStatuses } = useAppSelector((state) => state.jobStatus);
  const dispatch = useAppDispatch();

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    dispatch(jobStatusActions.changeJobStatusId(itemId));
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(jobStatusActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = async () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(jobStatusActions.changeMenuSelectId(menuSelectedId));
      dispatch(jobStatusActions.changeOpenAlertDelete(true));
    }
  };

  const handleClick = (event: React.ChangeEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelectAll(jobStatuses.length === newSelected.length);
    setSelected(newSelected);
    setSelectedIds(newSelected);
  };

  const handleItemChange = async (item: any) => {
    await dispatch(handleUpdateposition({ id: item.$id, params: { position: item.position } })).unwrap();
    dispatch(jobStatusActions.changeNeedRefreshData(true));
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      setSelected(jobStatuses.map((item) => item.id));
      setSelectedIds(jobStatuses.map((item) => item.id));
    } else {
      setSelected([]);
      setSelectedIds([]);
    }
  };

  return (
    <Box className={cx('equipment-list-container')}>
      <Box className={'flex justify-between items-center gap-[20px] my-3 w-full bg-[#ffffff]'}>
        <Box className={'w-[10px]'}></Box>
        <Box className='gap-[30px] w-full flex items-center'>
          <Box className='flex-[0_0_2%]'>
            <Checkbox className='w-[20px] h-[20px]' color='primary' checked={selectAll} onChange={handleSelectAll} />
          </Box>
          <Box className='flex-[0_0_45%] text-[16px]/[24px] font-[500] text-[#0d062d]'>{t('properties.name')}</Box>
          <Box className='flex-[0_0_45%] text-[16px]/[24px] font-[500] text-[#0d062d]'>{t('properties.createdAt')}</Box>
          <Box className='flex-[0_0_3%]'>
            <IconButton onClick={(event) => {}}>
              <MenuVerticalIcon className='w-[19px] h-[19px] invisible' />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <BaseSort
        onItemChange={handleItemChange}
        data={jobStatuses}
        search={() => true}
        render={(data, index, onItemChange) => {
          return (
            <Box className={'flex justify-between items-center gap-[20px] my-3 w-full bg-[#ffffff] '} key={index}>
              <Box className='gap-[30px] w-full flex items-center '>
                <Box className='flex-[0_0_2%] mt-[20px]'>
                  <Checkbox
                    className='w-[20px] h-[20px]'
                    color='primary'
                    onChange={(event) => {
                      handleClick(event, data?.id);
                    }}
                    checked={isSelected(data?.id)}
                  />
                </Box>
                <Box className='flex-[0_0_45%] font-[500]'>
                  <Box
                    className='inline-block rounded-[6px] px-[10px] py-[4px] text-[14px] leading-[20px]  mt-[20px]'
                    style={{
                      color: data?.color,
                      backgroundColor: data?.backgroundColor,
                    }}
                  >
                    {data?.name}
                  </Box>
                </Box>

                <Box className='flex-[0_0_45%] font-[500]'>{dayjs(data?.createdAt).format('MMM DD, YYYY')}</Box>
                {(permissions[PERMISSIONS.projectStatus_delete] ||
                  permissions[PERMISSIONS.projectStatus_edit] ||
                  permissions[PERMISSIONS.projectStatus_view]) && (
                  <Box className='flex-[0_0_3%]'>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, data?.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          );
        }}
      />

      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
      />
    </Box>
  );
}

export default memo(JobStatusList);
